import React, { useEffect, useState } from "react";
import { api, formatError, ROLE_LABEL, fmtDate } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, EmptyState, Badge, SearchBar, normalize } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound } from "lucide-react";

const empty = { email: "", password: "", name: "", role: "setor", sector_id: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const [u, s] = await Promise.all([api.get("/users"), api.get("/sectors")]);
    setItems(u.data); setSectors(s.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, sector_id: form.sector_id || null };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/auth/register", { ...form, sector_id: form.sector_id || null });
      }
      toast.success(editing ? "Atualizado" : "Usuário criado");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (id === me?.id) { toast.error("Não pode excluir você mesmo"); return; }
    if (!confirm("Excluir usuário?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Excluído"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const filtered = items.filter((u) => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (!q) return true;
    const nq = normalize(q);
    return normalize(u.name).includes(nq) || normalize(u.email).includes(nq) || normalize(u.sector_nome).includes(nq);
  });

  return (
    <div data-testid="users-page">
      <SectionTitle action={<Btn onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-user-btn"><Plus className="w-4 h-4" />Novo Usuário</Btn>}>
        Usuários do Sistema
      </SectionTitle>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar por nome, email ou setor..." testid="users-search" />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data" data-testid="users-role-filter">
          <option value="">Todos os perfis</option>
          {Object.entries(ROLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Nome</th><th>Email</th><th>Perfil</th><th>Setor</th><th>Status</th><th>Criado em</th><th className="w-24">Ações</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <EmptyState message={q || roleFilter ? "Nenhum resultado." : "Nenhum usuário."} cols={7} />}
            {filtered.map((u) => (
              <tr key={u.id}>
                <td className="font-medium">{u.name}</td>
                <td className="font-mono-data text-xs">{u.email}</td>
                <td><Badge variant={u.role === "admin" ? "danger" : u.role === "almoxarife" ? "orange" : u.role === "presidencia" ? "green" : "outline"}>{ROLE_LABEL[u.role]}</Badge></td>
                <td className="text-sm">{u.sector_nome || "—"}</td>
                <td>{u.active ? <Badge variant="success">Ativo</Badge> : <Badge>Inativo</Badge>}</td>
                <td className="font-mono-data text-xs">{fmtDate(u.created_at)}</td>
                <td><div className="flex gap-1">
                  <button onClick={() => { setEditing(u); setForm({ name: u.name, email: u.email, role: u.role, sector_id: u.sector_id || "", password: "" }); setOpen(true); }} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]"><Pencil className="w-3.5 h-3.5" /></button>
                  {u.id !== me?.id && <button onClick={() => remove(u.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Usuário" : "Novo Usuário"} size="md">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="user-form">
          <Field label="Nome *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} colspan />
          {!editing && <Field label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} colspan />}
          <Field label={editing ? "Nova senha (em branco mantém)" : "Senha *"} type="password" required={!editing} value={form.password} onChange={(v) => setForm({ ...form, password: v })} colspan />
          <SelectField label="Perfil *" required value={form.role} onChange={(v) => setForm({ ...form, role: v })}
            options={Object.entries(ROLE_LABEL).map(([k, l]) => ({ value: k, label: l }))} />
          <SelectField label="Setor" value={form.sector_id} onChange={(v) => setForm({ ...form, sector_id: v })}
            options={sectors.map((s) => ({ value: s.id, label: s.nome }))} />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-user-btn"><KeyRound className="w-4 h-4" />{editing ? "Salvar" : "Criar"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
